import {
	confirmSignIn,
	fetchAuthSession,
	fetchMFAPreference,
	getCurrentUser,
	setUpTOTP,
	signIn,
	updateMFAPreference,
	verifyTOTPSetup
} from '@aws-amplify/auth';
import axios, { setAuth } from '../../utilities/axios';
import ENUMS from '../../helpers/enums';
import { humanReadableServerErrors } from '../../utilities/humanReadableServerErrors';
import { ofType } from 'redux-observable';
import { of, merge } from 'rxjs';
import { mergeMap, switchMap, catchError } from 'rxjs/operators';
import {
	failureMessage,
	successMessage
} from '../Notifications/notificationsSlice';
import { primaryApi } from '../../utilities/apiEndpointUtility';

import { getUser } from '../User/userSlice';
import { getMetrics } from '../Metrics/metricsSlice';

import {
	authUser,
	authUserCompleted,
	authUserFailed,
	authUserLoginFailure,
	authUserChallenge,
	authUserValidateMfa,
	authUserValidateMfaCompleted,
	authUserValidateMfaFailed,
	getMfaSetupCode,
	getMfaSetupCodeCompleted,
	getMfaSetupCodeFailed,
	activateMfa,
	activateMfaCompleted,
	activateMfaFailed,
	deactivateMfa,
	deactivateMfaCompleted,
	deactivateMfaFailed,
	invalidateAuth,
	invalidateAuthCompleted,
	invalidateAuthFailed
} from './authSlice';

export const invalidateAuthEpic = (action$) =>
	action$.pipe(
		ofType(invalidateAuth.type),
		mergeMap(async () => {
			const response = await axios.post(
				`${primaryApi()}/api/v1/authorization/invalidate`
			);

			if (response.status !== 200) {
				throw response.data;
			}

			return response.data;
		}),
		switchMap((res) => [invalidateAuthCompleted(res)]),
		catchError((error, source) =>
			merge(
				of(invalidateAuthFailed(error.message), failureMessage(error.message)),
				source
			)
		)
	);

export const authUserEpic = (action$) =>
	action$.pipe(
		ofType(authUser.type),
		mergeMap(async (action) => {
			try {
				const { email, password } = action.payload;

				const request = await signIn({ username: email, password });

				if (!request.isSignedIn) {
					return {
						challengeName: request.nextStep.signInStep
					};
				} else {
					let user = await getCurrentUser();

					const { tokens } = await fetchAuthSession();
					const userMfaPreference = await fetchMFAPreference();

					if (request.isSignedIn) {
						const accessToken = tokens.accessToken.toString();
						setAuth(accessToken);
					}

					user.preferredMFA = userMfaPreference.preferred || ENUMS.MFA.NOMFA;

					user = Object.assign({}, user, request);

					return {
						user
					};
				}
			} catch (e) {
				const error = humanReadableServerErrors(e);
				return {
					error
				};
			}
		}),
		switchMap((res) => {
			const switchActionReturn = [];

			if (res.challengeName) {
				switchActionReturn.push(authUserChallenge(res));
			} else {
				if (res.error) {
					switchActionReturn.push(authUserLoginFailure(res.error));
				} else {
					// @INFO: If you're trying to load things on refresh, go to the "useSession" hook.
					switchActionReturn.push(authUserCompleted(res.user));
					switchActionReturn.push(getUser());
					switchActionReturn.push(getMetrics());
				}
			}

			return switchActionReturn;
		}),
		catchError((error, source) => {
			return merge(
				of(authUserFailed(error.message), failureMessage(error.message)),
				source
			);
		})
	);

export const verifyMultiFactorAuthCodeEpic = (action$, state$) =>
	action$.pipe(
		ofType(authUserValidateMfa.type),
		mergeMap(async (action) => {
			const challengeAnswer = action.payload;

			const signIn = await confirmSignIn({
				challengeResponse: challengeAnswer
			});

			if (!signIn.isSignedIn) {
				throw new Error('MFA Code Incorrect');
			}

			const user = await getCurrentUser();
			const { tokens } = await fetchAuthSession();
			const accessToken = tokens.accessToken.toString();
			setAuth(accessToken);

			return {
				user
			};
		}),
		switchMap((res) => [
			authUserCompleted(res.user),
			authUserValidateMfaCompleted(true)
		]),
		catchError((error, source) => {
			return merge(
				of(
					authUserValidateMfaFailed(error.message),
					failureMessage(error.message)
				),
				source
			);
		})
	);

export const getMultiFactorAuthSetupCodeEpic = (action$) =>
	action$.pipe(
		ofType(getMfaSetupCode.type),
		mergeMap(async () => {
			const user = await getCurrentUser();
			const username = user.signInDetails.loginId;
			const totpSetupDetails = await setUpTOTP();

			return {
				code: totpSetupDetails.sharedSecret,
				setupString: totpSetupDetails.getSetupUri('Meistro.io', username)
			};
		}),
		switchMap((res) => [getMfaSetupCodeCompleted(res)]),
		catchError((error, source) => {
			return merge(
				of(getMfaSetupCodeFailed(error.message), failureMessage(error.message)),
				source
			);
		})
	);

export const activateMfaEpic = (action$) =>
	action$.pipe(
		ofType(activateMfa.type),
		mergeMap(async (action) => {
			const challengeAnswer = action.payload;

			await verifyTOTPSetup({ code: challengeAnswer });
			await updateMFAPreference({ totp: 'PREFERRED' });

			const getUpdatedUser = await getCurrentUser();
			const userMfaPreference = await fetchMFAPreference();
			getUpdatedUser.preferredMFA =
				userMfaPreference.preferred || ENUMS.MFA.NOMFA;

			return getUpdatedUser;
		}),
		switchMap((res) => [
			activateMfaCompleted(res),
			// successMessage(i18n.t('notifications.mfaActivateSuccess'))
			successMessage('MFA Successfully Added') // @TODO: Fix this translation
		]),
		catchError((error, source) => {
			return merge(of(activateMfaFailed(error.message)), source);
		})
	);

export const deactivateMfaEpic = (action$) =>
	action$.pipe(
		ofType(deactivateMfa.type),
		mergeMap(async (action) => {
			const challengeAnswer = action.payload;

			await verifyTOTPSetup({ code: challengeAnswer });
			await updateMFAPreference({ totp: 'DISABLED' });

			const getUpdatedUser = await getCurrentUser();
			const userMfaPreference = await fetchMFAPreference();
			getUpdatedUser.preferredMFA =
				userMfaPreference.preferred || ENUMS.MFA.NOMFA;

			return getUpdatedUser;
		}),
		switchMap((res) => [
			deactivateMfaCompleted(res),
			// successMessage(i18n.t('notifications.mfaDeactivateSuccess'))
			successMessage('MFA Successfully Removed') // @TODO: Fix this translation
		]),
		catchError((error, source) => {
			return merge(of(deactivateMfaFailed(error.message)), source);
		})
	);

export default [
	activateMfaEpic,
	authUserEpic,
	deactivateMfaEpic,
	getMultiFactorAuthSetupCodeEpic,
	invalidateAuthEpic,
	verifyMultiFactorAuthCodeEpic
];
